import type { SeriesFrame } from './seriesCatalog'

/**
 * Что серия положит в архив и о чём спросит человека.
 *
 * Правило владельца (18.08.2026): картинку, под которой нет данных, не отдаём
 * вовсе — её нечем проверить, а пустое поле на ней неотличимо от честного
 * «здесь ничего нет».
 *
 * Но пропажа пропаже рознь, и в этом весь смысл разделения. Тайлов нет при
 * живом поле — это секунды отрисовки, и ждать их разумно. Поля нет в базе —
 * ждать нечего: полный пайплайн стоит 44 минуты на дату, и нажатие кнопки не
 * может его стоить. Поэтому человеку предлагается ВЫБОР, а не отказ.
 */

/** Что мы узнали о кадре, ещё ничего не рисуя. */
export type FrameVerdict =
  /** Тайлы на месте — снимать можно сейчас. */
  | 'ready'
  /** Поле есть, тайлов нет: дорисовать и дождаться. */
  | 'needs-render'
  /** Поля за дату нет вовсе. */
  | 'no-data'
  /** Спросить не смогли: сеть, отказ ручки, слой закрыт для кабинета. */
  | 'unknown'

export interface FrameState {
  frame: SeriesFrame
  verdict: FrameVerdict
}

export type SeriesChoice =
  /** Всё готово — снимаем без вопросов. */
  | 'go'
  /** Часть кадров можно дорисовать: подождать или взять готовые. */
  | 'wait-or-partial'
  /** Ждать нечего, но готовые кадры есть. */
  | 'partial-only'
  /** Отдавать нечего. */
  | 'nothing'

export interface SeriesPlan {
  ready: SeriesFrame[]
  renderable: SeriesFrame[]
  /** Кадры, которых в архиве не будет ни при каком выборе. */
  missing: SeriesFrame[]
  choice: SeriesChoice
}

export function seriesPlan(states: FrameState[]): SeriesPlan {
  const pick = (...v: FrameVerdict[]) =>
    states.filter(s => v.includes(s.verdict)).map(s => s.frame)

  const ready = pick('ready')
  const renderable = pick('needs-render')
  // Незнание кладём к пропавшим, а не к готовым: доказательства данных у нас
  // нет, а приняв молчание за «да», мы отдали бы ровно ту картинку, которую
  // запретили.
  const missing = pick('no-data', 'unknown')

  const choice: SeriesChoice = renderable.length > 0
    ? 'wait-or-partial'
    : missing.length === 0
      ? 'go'
      : ready.length > 0 ? 'partial-only' : 'nothing'

  return { ready, renderable, missing, choice }
}
